import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/lib/seo';
import { ARTICLES } from '@/modules/marketing/articles';
import { MarketingPage } from '@/modules/marketing/MarketingPage';
import { PAGES } from '@/seo/pages';

/**
 * Index of the long-form articles, one page per category.
 *
 * Built from `ARTICLES` rather than a hand-kept list, so an article that exists is listed and an
 * article that does not cannot be linked. The previous features index was a second copy of the
 * landing page's pitch and was removed for that reason; this is a table of contents, which is a
 * different thing — it sells nothing and every row goes somewhere real.
 */
function Index({
  category,
  eyebrow,
  title,
  standfirst,
}: {
  category: 'features' | 'monitoring';
  eyebrow: string;
  title: string;
  standfirst: string;
}) {
  const articles = ARTICLES.filter((article) => article.category === category);

  return (
    <MarketingPage eyebrow={eyebrow} title={title} standfirst={standfirst} wide>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {articles.map((article) => (
          <Link
            key={article.slug}
            to={`/${article.category}/${article.slug}`}
            className="border-border/60 hover:border-primary/40 hover:bg-muted/20 group flex flex-col rounded-xl border p-6 transition-colors"
          >
            <span className="bg-primary/10 text-primary inline-flex size-9 items-center justify-center rounded-lg">
              <article.icon className="size-4.5" aria-hidden />
            </span>
            <h2 className="mt-4 font-medium tracking-tight">{article.title}</h2>
            {/* The meta description is written to be read on its own, which is exactly the job
                here too. Falling back to the standfirst keeps a card from being empty. */}
            <p className="text-muted-foreground mt-3 flex-1 text-sm leading-relaxed">
              {article.metaDescription ?? article.standfirst}
            </p>
            <span className="text-primary mt-5 inline-flex items-center gap-1.5 text-sm font-medium">
              Learn more
              <span className="sr-only"> about {article.title}</span>
              <ArrowRight
                className="size-4 transition-transform group-hover:translate-x-0.5"
                aria-hidden
              />
            </span>
          </Link>
        ))}
      </div>
    </MarketingPage>
  );
}

export function FeatureArticlesPage() {
  usePageMeta(PAGES.features);

  return (
    <Index
      category="features"
      eyebrow="Features"
      title="Platform Features"
      standfirst="How automation, monitoring, Auto-Fix and backups work in detail, including what each one does not do."
    />
  );
}

export function MonitoringGuidesPage() {
  usePageMeta(PAGES.monitoringGuides);

  return (
    <Index
      category="monitoring"
      eyebrow="Monitoring"
      title="Monitoring Guides"
      standfirst="What website, API, SSL certificate, DNS, domain expiry and port checks detect, and how to configure each one."
    />
  );
}
